import React from 'react';
import { ScanIcons } from 'src/constants/general-media';
import { AlertLevels } from 'src/models/alert-model';
import { LabelScanResult, LabelScanResultType, ScanResultType } from 'src/models/unload';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import UnloadScanStep, { getFailureMessage, getSidelinedMessage,
    ScanResultMessage } from 'src/components/pages/unload-page/common/UnloadScanStep';

// A Record, so an outcome without a message fails to compile
const MESSAGE_BY_RESULT_TYPE: Record<LabelScanResultType,
    (scan: LabelScanResult, chain: string[]) => ScanResultMessage> = {
    [ScanResultType.RECEIVED]: (scan, chain) => ({ type: AlertLevels.SUCCESS, title: AppStrings.UNLOAD_RECEIVED_TITLE,
        lines: [scan.value], detail: [...chain, scan.value].join(AppStrings.UNLOAD_CHAIN_SEPARATOR) }),
    [ScanResultType.RECEIVED_AS_ORPHAN]: scan => ({ type: AlertLevels.SUCCESS,
        title: AppStrings.UNLOAD_RECEIVED_AS_ORPHAN_TITLE, lines: [scan.value] }),
    [ScanResultType.SIDELINED]: getSidelinedMessage,
    [ScanResultType.REFUSED]: getFailureMessage,
    [ScanResultType.SYSTEM_ERROR]: getFailureMessage
};

interface UnloadContainerScanPageProps {
    // What the loop scans into, over the count of what it has received there
    heading: string;
    // What has been received there, under it
    count?: string;
    // The ids a received label is chained under
    chain: string[];
    // The latest label scanned, with its result
    lastLabel?: LabelScanResult;
    // Shown in place of the latest label's result
    notice?: ScanResultMessage;
    // Inside a pallet, where only cartons are scanned
    cartons?: boolean;
    // The orphan loop, which scans with no trailer
    orphan?: boolean;
    // Takes each label scanned
    onSubmit: (label: string) => void;
}

const UnloadContainerScanPage = ({ heading, count, chain, lastLabel, notice, cartons, orphan, onSubmit }:
                               UnloadContainerScanPageProps) => {
    const result = notice ?? (lastLabel && MESSAGE_BY_RESULT_TYPE[lastLabel.outcome](lastLabel, chain));
    return (
        <UnloadScanStep icon={ScanIcons.SCAN_HANDHELD_ICON} heading={heading} subheading={count}
                        fieldTestId="unload-container-scan-field"
                        fieldLabel={cartons ? AppStrings.UNLOAD_CARTON_FIELD_LABEL
                            : AppStrings.UNLOAD_CONTAINER_FIELD_LABEL}
                        placeholder={cartons ? AppStrings.UNLOAD_CARTON_FIELD_PLACEHOLDER
                            : orphan ? AppStrings.UNLOAD_ORPHAN_FIELD_PLACEHOLDER
                            : AppStrings.UNLOAD_CONTAINER_FIELD_PLACEHOLDER}
                        prompt={cartons ? AppStrings.UNLOAD_CARTON_IDLE_PROMPT
                            : AppStrings.UNLOAD_CONTAINER_IDLE_PROMPT}
                        onSubmit={onSubmit} message={result}/>
    );
};

// Sideline's step: any barcode is a sideline ID, and one not recorded shows its result here
export const ScanSidelinePage = ({ lastSideline, onSubmit }:
                                     { lastSideline?: LabelScanResult, onSubmit: (id: string) => void }) => (
    <UnloadScanStep icon={ScanIcons.SCAN_HANDHELD_ICON} heading={AppStrings.UNLOAD_SIDELINE_HEADING}
                    fieldTestId="unload-sideline-scan-field" fieldLabel={AppStrings.UNLOAD_SIDELINE_FIELD_LABEL}
                    message={lastSideline && MESSAGE_BY_RESULT_TYPE[lastSideline.outcome](lastSideline, [])}
                    onSubmit={onSubmit}/>
);

export default UnloadContainerScanPage;
